// ═════════════════════════════════════════════════════════════════════════════
// NHCX Communication API client
//
// Contract: COMMUNICATION_UI_INTEGRATION.md (repo root).
//
//   GET  /app/api/insurance/responses/                      → inbox
//   GET  /app/api/insurance/response/{id}/                  → one cached request
//   POST /app/api/insurance/communication/on_request/       → reply
//   POST /app/api/insurance/dummy-payer/trigger/            → sandbox Query
//
// The inbound POST /communication/request is payer-initiated — the UI never
// calls it. Reply takes no Org-Code / App-Mode. The verdict does not come back
// here; it lands on the original pre-auth / claim correlation id.
// ═════════════════════════════════════════════════════════════════════════════

import axios from "axios";
import {
  nhcxUrl,
  nhcxAckHeaders,
  listNhcxResponses,
  pollNhcxResponse,
  normalizeNhcxError,
} from "./nhcx-client";
import { triggerDummyPayer } from "./preauth-claim-api";

export { listNhcxResponses, pollNhcxResponse, triggerDummyPayer };

export function isCommunication(item) {
  return String(item?.use_case || "") === "communication";
}

export async function listCommunications() {
  const all = await listNhcxResponses();
  return all.filter(isCommunication);
}

export async function replyToCommunication({
  correlationId,
  contentStrings,
  attachments,
  overridePatientAbhaId,
}) {
  const body = { correlation_id: correlationId };
  const strings = (contentStrings || []).map((s) => String(s || "").trim()).filter(Boolean);
  if (strings.length) body.content_strings = strings;
  if (attachments && attachments.length) body.attachments = attachments;
  if (String(overridePatientAbhaId || "").trim()) {
    body.override_patient_abha_id = String(overridePatientAbhaId).trim();
  }
  const res = await axios.post(nhcxUrl("/communication/on_request/"), body, {
    headers: nhcxAckHeaders(),
  });
  return res.data;
}

export function normalizeCommunicationError(err, kind = "reply") {
  if (kind === "list") {
    return normalizeNhcxError(err, {
      404: "No cached communication requests on the insurance service yet.",
      502: "The NHCX gateway is unreachable. Try again in a moment.",
    });
  }
  if (kind === "trigger") {
    return normalizeNhcxError(err, {
      404: "No pre-auth or claim is queued for that correlation id yet. Wait ~10 seconds after submit, then trigger Query.",
      502: "The NHCX gateway is unreachable. Try again in a moment.",
    });
  }
  return normalizeNhcxError(err, {
    404:
      "This request is gone from the service's memory (a restart clears it). The correlation id alone cannot reconstruct the payer identity — the payer will need to re-send. Retrying will not work.",
    409: "This request is not in a state that can be replied to. Refresh the inbox.",
    500: "Organisation details could not be recovered from the cached request. Quote the correlation id to support.",
    502: "The NHCX gateway is unreachable. Try again in a moment.",
  });
}
